import React, { useState, useEffect } from 'react';
import { Header, Loader, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import ReactGA from 'react-ga4';
import toast from 'react-hot-toast';

import { DealStates } from '~/constants';
import { indexRfisByPortfolioId } from '~/api/requestForInformation';
import * as portfolioApi from '~/api/portfolios';
import * as dealSpreadsheetApi from '~/api/dealSpreadsheet';
import { logUserActivity, UserActivityTypes } from '~/api/user';
import { usePortfolioContext } from '~/hooks';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { useDataroomQuery } from '~/requests/dataroom/useDataroomQuery';
import { getDataroomCounts } from '~/utils/calculators/dataroomCounts';
import { downloadPortfolioExcel, generateDealSpreadsheet } from '~/utils';
import '../styles.scss';

const PortfolioChecklist: React.FC = () => {
    const { portfolio, portfolioDisplayValues, portfolioState, portfolioTotals, displayBlobs, locations } = usePortfolioContext();
    const { permissions } = usePermissions();
    const bidDeadline = portfolio?.bidDeadline || null;
    const [rfis, setRfis] = useState<any>();
    const [showChecklist, setShowChecklist] = useState<boolean>(true);
    const [hasSummaryDoc, setHasSummaryDoc] = useState<boolean | undefined>(undefined);
    const activity = portfolio?.currentUserActivity;
    // Hide the "Access data rooms" checklist item when the dataroom is empty for an
    // investor pre-diligence, matching when the dataroom tab is hidden.
    const { data: dataroom } = useDataroomQuery({
        onError() { /* user may not have dataroom access */ },
    });
    const isInvestor = !permissions?.isAdmin && permissions?.hasPortfolioInvestorAccess;
    const dataroomCounts = dataroom ? getDataroomCounts(dataroom.documents) : null;
    const showDataroomTab = permissions?.canReadDataroom
        && !(isInvestor && portfolioState?.latestState < DealStates.IN_DILIGENCE && !dataroomCounts?.uploaded);
    const fullChecklist = [
        { key: 'downloaded-summary', title: 'Download Deal summary', description: 'Download the Deal summary document for this deal.', isComplete: !!(activity?.downloadedSummary), linkTo: portfolio?.id ? `/portfolio/${portfolio.id}/review#key-downloads` : undefined, buttonText: 'Download' },
        { key: 'downloaded-spreadsheet', title: 'Download deal spreadsheet', description: 'Download the deal spreadsheet for your records.', isComplete: !!(activity?.downloadedSpreadsheet), linkTo: portfolio?.id ? `/portfolio/${portfolio.id}/review#key-downloads` : undefined, buttonText: 'Download' },
        { key: 'accessed-dataroom', title: 'Access data rooms', description: 'View or download documents from the deal data rooms.', isComplete: !!(activity?.downloadedAnyDataroomDocs), linkTo: portfolio?.id ? `/portfolio/${portfolio.id}/dataroom` : undefined, buttonText: 'Go there now' },
        { key: 'supporting-info', title: 'Provide supporting information', description: 'The seller has requested some additional details about your company and experience.', isComplete: false, linkTo: 'bidding#supporting-info', buttonText: 'Answer now' },
        { key: 'bidding', title: 'Submit your non-binding bids ' + (bidDeadline ? 'by ' + format(parseISO(bidDeadline), 'MMM d') : ''), description: 'Submit your non-binding bids to the seller' + (bidDeadline ? ' by the bid deadline' : '') + ' to be considered for partnership.', isComplete: false },
    ];
    const [checklistItems, setChecklistItems] = useState<any[]>(fullChecklist);
    const [loading, setLoading] = useState<boolean>(true);

    useEffect(() => {
        if (portfolio?.id) {
            getPortfolioRfis();
        }
        if (
            portfolioState?.latestState >= DealStates.IN_DILIGENCE ||
            portfolioState?.isDeclined ||
            portfolioState?.latestState < DealStates.IN_REVIEW ||
            portfolioDisplayValues?.status === 'Diligence'
        ) {
            setShowChecklist(false);
        } else {
            setShowChecklist(true);
        }
    }, [portfolio?.id, portfolioState?.latestState, portfolioState?.isDeclined, portfolioDisplayValues?.status]);

    useEffect(() => {
        const biddingComplete = portfolio?.projects && portfolio.projects.length > 0 &&
            portfolio.projects.every((project: any) =>
                (project.projectBids && project.projectBids.length > 0) ||
                project.status === 'DECLINED_TO_BID'
            );

        setChecklistItems(prevItems => {
            const updatedItems = [...prevItems];
            const biddingIndex = updatedItems.findIndex(item => item.key === 'bidding');
            if (biddingIndex !== -1) {
                updatedItems[biddingIndex] = { ...updatedItems[biddingIndex], isComplete: biddingComplete };
            }
            return updatedItems;
        });
    }, [portfolio?.projects]);

    useEffect(() => {
        if (rfis === undefined) return;
        if (rfis.length > 0) {
            const allRfisHaveResponses = rfis.every((rfi: any) =>
                Array.isArray(rfi.responses) && rfi.responses.length > 0
            );

            setChecklistItems(prevItems => {
                const updatedItems = [...prevItems];
                const supportingInfoIndex = updatedItems.findIndex(item => item.key === 'supporting-info');
                if (supportingInfoIndex !== -1) {
                    updatedItems[supportingInfoIndex] = { ...updatedItems[supportingInfoIndex], isComplete: allRfisHaveResponses };
                }
                return updatedItems;
            });
        } else {
            setChecklistItems(prevItems => prevItems.filter(item => item.key !== 'supporting-info'));
        }
    }, [rfis]);

    useEffect(() => {
        const activity = portfolio?.currentUserActivity;
        if (activity == null) return;
        setChecklistItems(prevItems => {
            const updated = [...prevItems];
            const summaryIdx = updated.findIndex(item => item.key === 'downloaded-summary');
            const spreadsheetIdx = updated.findIndex(item => item.key === 'downloaded-spreadsheet');
            const dataroomIdx = updated.findIndex(item => item.key === 'accessed-dataroom');
            if (summaryIdx !== -1) updated[summaryIdx] = { ...updated[summaryIdx], isComplete: !!activity.downloadedSummary };
            if (spreadsheetIdx !== -1) updated[spreadsheetIdx] = { ...updated[spreadsheetIdx], isComplete: !!activity.downloadedSpreadsheet };
            if (dataroomIdx !== -1) updated[dataroomIdx] = { ...updated[dataroomIdx], isComplete: !!activity.downloadedAnyDataroomDocs };
            return updated;
        });
    }, [portfolio?.currentUserActivity]);

    useEffect(() => {
        if (!portfolio?.id) return;
        const portfolioId = portfolio.parentId ?? portfolio.id;
        portfolioApi.getPortfolioSummaryDownloadUrl(portfolioId)
            .then((data: { presignedUrl?: string }) => setHasSummaryDoc(!!data?.presignedUrl))
            .catch(() => setHasSummaryDoc(false));
    }, [portfolio?.id, portfolio?.parentId]);

    const getPortfolioRfis = () => {
        if (portfolio?.id) {
            setLoading(true);
            indexRfisByPortfolioId(portfolio.id).then(response => {
                setRfis(response);
                setLoading(false);
            }).catch((error) => {
                console.error('Error fetching portfolio RFIs:', error);
                setLoading(false);
            });
        }
    };

    const [summaryDownloading, setSummaryDownloading] = useState(false);
    const handleSummaryDownload = async () => {
        if (!portfolio?.id || summaryDownloading) return;
        const portfolioId = portfolio.parentId ?? portfolio.id;
        setSummaryDownloading(true);
        try {
            ReactGA.event({
                category: 'Downloads',
                action: 'Download Conductor summary document',
                label: portfolio?.name || 'Unknown portfolio',
            });
            logUserActivity({
                activityType: UserActivityTypes.SUMMARY_DOCUMENT_DOWNLOAD,
                portfolioId,
            });
            const data = await portfolioApi.getPortfolioSummaryDownloadUrl(portfolioId);
            if (data?.presignedUrl) {
                setChecklistItems((prev) => {
                    const next = [...prev];
                    const i = next.findIndex((item) => item.key === 'downloaded-summary');
                    if (i !== -1) next[i] = { ...next[i], isComplete: true };
                    return next;
                });
                window.location.href = data.presignedUrl;
            } else {
                toast.error('Summary document is not available.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Error occurred downloading the document. Please refresh the page and try again.');
        } finally {
            setSummaryDownloading(false);
        }
    };

    const [spreadsheetDownloading, setSpreadsheetDownloading] = useState(false);
    const handleSpreadsheetDownload = async () => {
        if (!portfolio?.id || spreadsheetDownloading) return;
        const dealId = portfolio.parentId ?? portfolio.id;
        const dealType = 'PORTFOLIO';
        setSpreadsheetDownloading(true);
        try {
            ReactGA.event({
                category: 'Downloads',
                action: 'Download deal spreadsheet',
                label: portfolio?.name || 'Unknown portfolio',
            });
            logUserActivity({
                activityType: UserActivityTypes.DEAL_SPREADSHEET_DOWNLOAD,
                portfolioId: dealId,
            });
            let data: { presignedUrl?: string } | undefined;
            try {
                data = await dealSpreadsheetApi.getDealSpreadsheetDownloadUrl(dealId, dealType);
            } catch {
                data = undefined;
            }
            if (data?.presignedUrl) {
                setChecklistItems((prev) => {
                    const next = [...prev];
                    const i = next.findIndex((item) => item.key === 'downloaded-spreadsheet');
                    if (i !== -1) next[i] = { ...next[i], isComplete: true };
                    return next;
                });
                window.location.href = data.presignedUrl;
            } else {
                if (permissions?.isAdmin) {
                    await generateDealSpreadsheet(portfolio, portfolioTotals, displayBlobs);
                } else {
                    await downloadPortfolioExcel(portfolio, locations, portfolioTotals, displayBlobs, permissions);
                }
                setChecklistItems((prev) => {
                    const next = [...prev];
                    const i = next.findIndex((item) => item.key === 'downloaded-spreadsheet');
                    if (i !== -1) next[i] = { ...next[i], isComplete: true };
                    return next;
                });
            }
        } catch (err) {
            console.error(err);
            toast.error('Error occurred downloading the document. Please refresh the page and try again.');
        } finally {
            setSpreadsheetDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="card portfolio-checklist-container">
                <Loader active inline='centered'></Loader>
            </div>
        );
    }

    const displayedItems = checklistItems.filter(
        (item) => (item.key !== 'downloaded-summary' || hasSummaryDoc === true)
            && (item.key !== 'accessed-dataroom' || showDataroomTab)
    );

    return (
        !showChecklist ? null :
            <div className="card portfolio-checklist-container">
                <Header as='h2' className="portfolio-checklist-title">
                    {displayedItems.every(item => item.isComplete) ? 'All checklist items complete!' : 'Your deal checklist'}
                </Header>
                <div className="portfolio-activity-checklist-list portfolio-checklist-list">
                    {displayedItems.map((item) => (
                                <div
                                    key={item.key}
                                    className={`portfolio-activity-checklist-item ${item.isComplete ? 'complete' : 'incomplete'}`}
                                >
                                    <div className="portfolio-activity-checklist-item-content">
                                        {item.isComplete ? (
                                            <Icon name="check circle" color="green" />
                                        ) : (
                                            <Icon name="circle outline" />
                                        )}
                                        <span className="portfolio-activity-checklist-item-label">
                                            {item.title}
                                        </span>
                                        {!item.isComplete && (
                                            item.key === 'downloaded-summary' ? (
                                                <>
                                                    -
                                                    <button
                                                        type="button"
                                                        className="portfolio-checklist-action-link portfolio-checklist-action-button"
                                                        onClick={handleSummaryDownload}
                                                        disabled={summaryDownloading}
                                                    >
                                                        {summaryDownloading ? 'Downloading…' : (item.buttonText ?? 'Download')}
                                                    </button>
                                                </>
                                            ) : item.key === 'downloaded-spreadsheet' ? (
                                                <>
                                                    -
                                                    <button
                                                        type="button"
                                                        className="portfolio-checklist-action-link portfolio-checklist-action-button"
                                                        onClick={handleSpreadsheetDownload}
                                                        disabled={spreadsheetDownloading}
                                                    >
                                                        {spreadsheetDownloading ? 'Downloading…' : (item.buttonText ?? 'Download')}
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    -
                                                    <Link
                                                        to={item.linkTo ?? 'bidding'}
                                                        className="portfolio-checklist-action-link"
                                                    >
                                                        {item.buttonText ?? 'Get started'}
                                                    </Link>
                                                </>
                                            )
                                        )}
                                    </div>
                                </div>
                            ))}
                </div>
            </div>
    );
};

export default PortfolioChecklist;
